import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { ImageUpload } from '../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyCategory = {
  name_az: '', name_en: '', name_ru: '',
  description_az: '', description_en: '', description_ru: '',
  image_url: '', display_order: 0
};

const emptyItem = {
  category_id: '', name_az: '', name_en: '', name_ru: '',
  description_az: '', description_en: '', description_ru: '',
  price: '', discount_price: '', image_url: '', is_featured: false
};

export const MenuManagement = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [error, setError] = useState('');
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [itemForm, setItemForm] = useState(emptyItem);

  useEffect(() => {
    fetchData();
  }, [restaurantId]);

  const fetchData = async () => {
    try {
      const [restaurantRes, categoriesRes, itemsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/restaurants/${restaurantId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/menu/categories/${restaurantId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/menu/items/${restaurantId}`, { withCredentials: true }),
      ]);
      setRestaurant(restaurantRes.data);
      setCategories(categoriesRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      setError('Məlumatlar yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const openCategoryModal = (cat = null) => {
    setEditingCategory(cat);
    setCategoryForm(cat ? { ...emptyCategory, ...cat } : emptyCategory);
    setShowCategoryModal(true);
    setError('');
  };

  const openItemModal = (item = null, defaultCategoryId = '') => {
    setEditingItem(item);
    setItemForm(item ? { ...emptyItem, ...item, discount_price: item.discount_price ?? '', price: item.price ?? '' } : { ...emptyItem, category_id: defaultCategoryId });
    setShowItemModal(true);
    setError('');
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = { ...categoryForm, display_order: parseInt(categoryForm.display_order) || 0 };
      if (editingCategory) {
        await axios.put(`${BACKEND_URL}/api/menu/categories/${editingCategory.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/menu/categories?restaurant_id=${restaurantId}`, payload, { withCredentials: true });
      }
      setShowCategoryModal(false);
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        ...itemForm,
        price: parseFloat(itemForm.price),
        discount_price: itemForm.discount_price ? parseFloat(itemForm.discount_price) : null,
      };
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/menu/items/${editingItem.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/menu/items?restaurant_id=${restaurantId}`, payload, { withCredentials: true });
      }
      setShowItemModal(false);
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Kateqoriyanı silmək istədiyinizdən əminsiniz?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/menu/categories/${categoryId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert('Silinə bilmədi');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Məhsulu silmək istədiyinizdən əminsiniz?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/menu/items/${itemId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert('Silinə bilmədi');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const currencySymbol = restaurant?.currency_symbol || '₼';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <button onClick={() => navigate('/dashboard')} className="text-emerald-600 hover:underline mb-4">
          ← Əsas Səhifə
        </button>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800" data-testid="menu-management-title">
              {restaurant?.name} - Menyu İdarəsi
            </h1>
            <p className="text-gray-600 mt-1">Valyuta: {currencySymbol} ({restaurant?.currency})</p>
          </div>
          <div className="space-x-2">
            <button
              onClick={() => openCategoryModal()}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition"
              data-testid="add-category-button"
            >
              + Kateqoriya
            </button>
            <button
              onClick={() => openItemModal()}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition"
              data-testid="add-item-button"
            >
              + Məhsul
            </button>
            <button
              onClick={() => window.open(`/qr-menu/${restaurantId}`, '_blank')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
            >
              👁️ Menyuya Bax
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>
        )}

        <div className="space-y-6">
          {categories.map((category) => (
            <div key={category.id} className="bg-white rounded-xl shadow-sm p-6" data-testid={`category-${category.id}`}>
              <div className="flex items-start justify-between mb-4 gap-4">
                <div className="flex items-start space-x-3 flex-1">
                  {category.image_url && (
                    <img src={category.image_url} alt={category.name_az} className="w-16 h-16 rounded-lg object-cover" />
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">{category.name_az}</h2>
                    {(category.name_en || category.name_ru) && (
                      <p className="text-xs text-gray-500 mt-1">
                        EN: {category.name_en || '—'} | RU: {category.name_ru || '—'}
                      </p>
                    )}
                    {category.description_az && (
                      <p className="text-gray-600 text-sm mt-1">{category.description_az}</p>
                    )}
                  </div>
                </div>
                <div className="space-x-2 flex-shrink-0">
                  <button
                    onClick={() => openItemModal(null, category.id)}
                    className="px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm rounded transition"
                  >
                    + Məhsul
                  </button>
                  <button onClick={() => openCategoryModal(category)} className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 text-sm rounded transition">
                    Dəyiş
                  </button>
                  <button onClick={() => deleteCategory(category.id)} className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 text-sm rounded transition" data-testid={`delete-category-${category.id}`}>
                    Sil
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items
                  .filter((item) => item.category_id === category.id)
                  .map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition" data-testid={`menu-item-${item.id}`}>
                      {item.image_url && (
                        <img src={item.image_url} alt={item.name_az} className="w-full h-32 object-cover rounded-lg mb-3" />
                      )}
                      <h3 className="font-semibold text-gray-800 mb-1">{item.name_az}</h3>
                      {item.description_az && (
                        <p className="text-gray-600 text-sm mb-2 line-clamp-2">{item.description_az}</p>
                      )}
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-lg font-bold text-emerald-600" data-testid={`item-price-${item.id}`}>
                          {item.discount_price ? (
                            <>
                              <span className="line-through text-gray-400 text-sm mr-1">{item.price}</span>
                              {item.discount_price}
                            </>
                          ) : item.price} {currencySymbol}
                        </span>
                        <div className="space-x-1">
                          <button onClick={() => openItemModal(item)} className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs rounded transition">
                            Dəyiş
                          </button>
                          <button onClick={() => deleteItem(item.id)} className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs rounded transition" data-testid={`delete-item-${item.id}`}>
                            Sil
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              {items.filter((item) => item.category_id === category.id).length === 0 && (
                <p className="text-gray-500 text-center py-4">Bu kateqoriyada məhsul yoxdur</p>
              )}
            </div>
          ))}
        </div>

        {categories.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">Hələ kateqoriya əlavə edilməyib</p>
            <button onClick={() => openCategoryModal()} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition">
              İlk kateqoriyanı əlavə et
            </button>
          </div>
        )}
      </div>

      {/* CATEGORY MODAL */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 my-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {editingCategory ? 'Kateqoriyanı Dəyiş' : 'Yeni Kateqoriya'}
            </h2>
            <form onSubmit={handleCategorySubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad (AZ) *</label>
                  <input type="text" value={categoryForm.name_az} onChange={(e) => setCategoryForm({ ...categoryForm, name_az: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" required data-testid="category-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad (EN)</label>
                  <input type="text" value={categoryForm.name_en} onChange={(e) => setCategoryForm({ ...categoryForm, name_en: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad (RU)</label>
                  <input type="text" value={categoryForm.name_ru} onChange={(e) => setCategoryForm({ ...categoryForm, name_ru: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Təsvir (AZ)</label>
                <textarea value={categoryForm.description_az} onChange={(e) => setCategoryForm({ ...categoryForm, description_az: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" rows="2" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ImageUpload
                  value={categoryForm.image_url}
                  onChange={(url) => setCategoryForm({ ...categoryForm, image_url: url })}
                  label="Kateqoriya Şəkli"
                  testId="category-image"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sıralama</label>
                  <input type="number" value={categoryForm.display_order} onChange={(e) => setCategoryForm({ ...categoryForm, display_order: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div className="flex space-x-2 pt-2">
                <button type="button" onClick={() => setShowCategoryModal(false)} className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition">
                  İmtina
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition" data-testid="category-submit-button">
                  {editingCategory ? 'Yadda saxla' : 'Əlavə Et'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ITEM MODAL */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 my-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {editingItem ? 'Məhsulu Dəyiş' : 'Yeni Məhsul'}
            </h2>
            <form onSubmit={handleItemSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kateqoriya *</label>
                <select value={itemForm.category_id} onChange={(e) => setItemForm({ ...itemForm, category_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" required data-testid="item-category-select">
                  <option value="">Seçin</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name_az}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad (AZ) *</label>
                  <input type="text" value={itemForm.name_az} onChange={(e) => setItemForm({ ...itemForm, name_az: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" required data-testid="item-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad (EN)</label>
                  <input type="text" value={itemForm.name_en} onChange={(e) => setItemForm({ ...itemForm, name_en: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad (RU)</label>
                  <input type="text" value={itemForm.name_ru} onChange={(e) => setItemForm({ ...itemForm, name_ru: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Təsvir (AZ)</label>
                  <textarea value={itemForm.description_az} onChange={(e) => setItemForm({ ...itemForm, description_az: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" rows="2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Təsvir (EN)</label>
                  <textarea value={itemForm.description_en} onChange={(e) => setItemForm({ ...itemForm, description_en: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" rows="2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Təsvir (RU)</label>
                  <textarea value={itemForm.description_ru} onChange={(e) => setItemForm({ ...itemForm, description_ru: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" rows="2" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Qiymət ({currencySymbol}) *</label>
                  <input type="number" step="0.01" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" required data-testid="item-price-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Endirim qiyməti ({currencySymbol})</label>
                  <input type="number" step="0.01" value={itemForm.discount_price} onChange={(e) => setItemForm({ ...itemForm, discount_price: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500" placeholder="İstəyə bağlı" />
                </div>
              </div>
              <ImageUpload
                value={itemForm.image_url}
                onChange={(url) => setItemForm({ ...itemForm, image_url: url })}
                label="Məhsul Şəkli"
                testId="item-image"
              />
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="is_featured" checked={itemForm.is_featured} onChange={(e) => setItemForm({ ...itemForm, is_featured: e.target.checked })} className="w-4 h-4 text-emerald-600 rounded" />
                <label htmlFor="is_featured" className="text-sm font-medium text-gray-700">⭐ Seçilmiş məhsul kimi göstər</label>
              </div>
              <div className="flex space-x-2 pt-2">
                <button type="button" onClick={() => setShowItemModal(false)} className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition">
                  İmtina
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition" data-testid="item-submit-button">
                  {editingItem ? 'Yadda saxla' : 'Əlavə Et'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
